import { createContext, useContext, useState, useEffect } from 'react';

// ── Theme Presets ─────────────────────────────────────────────────────────────
const THEMES = {
  tsela: {
    dark: {
      '--t-bg0': '#0f172a',
      '--t-bg0g': '#0a1424',
      '--t-bg1': '#0a1424',
      '--t-bg2': 'rgba(25, 35, 55, 0.8)',
      '--t-bgGlass': 'rgba(25, 35, 55, 0.8)',
      '--t-bgGlassLt': 'rgba(25, 35, 55, 0.6)',
      '--t-surfaceSolid': '#1e293b',
      '--t-border': 'rgba(71, 85, 105, 0.6)',
      '--t-borderHi': 'rgba(96, 148, 234, 0.55)',
      '--t-white': '#f0f4fe',
      '--t-textLt': '#cbd5e1',
      '--t-muted': '#cbd5e1',
      '--t-accentSoft': '#1f2b3f',
      '--t-accent': '#6094ea',
      '--t-badgeBg': 'rgba(255, 255, 255, 0.05)',
      '--t-sliderBg': '#33455c',
      '--t-sliderActive': '#6094ea',
      '--t-bodyBg': 'linear-gradient(145deg, #0f172a 0%, #0a1424 100%)',
    },
    light: {
      '--t-bg0': '#f8fafc',
      '--t-bg0g': '#f0f5fa',
      '--t-bg1': '#f0f5fa',
      '--t-bg2': 'rgba(255, 255, 255, 0.75)',
      '--t-bgGlass': 'rgba(255, 255, 255, 0.75)',
      '--t-bgGlassLt': 'rgba(255, 255, 255, 0.55)',
      '--t-surfaceSolid': '#ffffff',
      '--t-border': 'rgba(203, 213, 225, 0.6)',
      '--t-borderHi': 'rgba(59, 130, 246, 0.45)',
      '--t-white': '#0b1e33',
      '--t-textLt': '#1e293b',
      '--t-muted': '#475569',
      '--t-accentSoft': '#e6edf6',
      '--t-accent': '#3b82f6',
      '--t-badgeBg': 'rgba(0, 0, 0, 0.06)',
      '--t-sliderBg': '#b9c9e0',
      '--t-sliderActive': '#3b82f6',
      '--t-bodyBg': 'linear-gradient(145deg, #f8fafc 0%, #f0f5fa 100%)',
    },
  },

  deep_glass: {
    dark: {
      '--t-bg0': '#081420',
      '--t-bg0g': '#06101a',
      '--t-bg1': 'rgba(8,20,32,0.6)',
      '--t-bg2': 'rgba(255,255,255,0.04)',
      '--t-bgGlass': 'rgba(255,255,255,0.04)',
      '--t-bgGlassLt': 'rgba(255,255,255,0.02)',
      '--t-surfaceSolid': '#0d1e30',
      '--t-border': 'rgba(255,255,255,0.08)',
      '--t-borderHi': 'rgba(255,255,255,0.14)',
      '--t-white': '#FEFEFE',
      '--t-textLt': 'rgba(255,255,255,0.88)',
      '--t-muted': 'rgba(255,255,255,0.45)',
      '--t-accentSoft': 'rgba(44,114,174,0.18)',
      '--t-accent': '#2C72AE',
      '--t-badgeBg': 'rgba(198,167,68,0.07)',
      '--t-sliderBg': '#0d1e30',
      '--t-sliderActive': '#C6A744',
      '--t-bodyBg': [
        'radial-gradient(ellipse at 15% 25%, rgba(44,114,174,0.45) 0%, transparent 50%)',
        'radial-gradient(ellipse at 85% 70%, rgba(95,152,196,0.28) 0%, transparent 50%)',
        'radial-gradient(ellipse at 50% 95%, rgba(198,167,68,0.12) 0%, transparent 40%)',
        'linear-gradient(155deg, #081420 0%, #0d1e30 50%, #06101a 100%)',
      ].join(', '),
    },
    // Horizon light theme
    light: {
      '--t-bg0': '#dce8f0',
      '--t-bg0g': '#b8cede',
      '--t-bg1': 'rgba(255,255,255,0.25)',
      '--t-bg2': 'rgba(255,255,255,0.55)',
      '--t-bgGlass': 'rgba(255,255,255,0.55)',
      '--t-bgGlassLt': 'rgba(255,255,255,0.40)',
      '--t-surfaceSolid': '#FEFEFE',
      '--t-border': 'rgba(255,255,255,0.75)',
      '--t-borderHi': 'rgba(255,255,255,0.90)',
      '--t-white': '#040909',
      '--t-textLt': '#040909',
      '--t-muted': 'rgba(4,9,9,0.65)',
      '--t-accentSoft': 'rgba(44,114,174,0.10)',
      '--t-accent': '#2C72AE',
      '--t-badgeBg': 'rgba(198,167,68,0.10)',
      '--t-sliderBg': 'rgba(255,255,255,0.40)',
      '--t-sliderActive': '#2C72AE',
      '--t-bodyBg': [
        'radial-gradient(ellipse at 10% 10%, rgba(44,114,174,0.35)  0%, transparent 55%)',
        'radial-gradient(ellipse at 90% 80%, rgba(172,190,202,0.50) 0%, transparent 55%)',
        'radial-gradient(ellipse at 60% 30%, rgba(95,152,196,0.25)  0%, transparent 45%)',
        'linear-gradient(155deg, #dce8f0 0%, #c8d9e8 40%, #b8cede 100%)',
      ].join(', '),
    },
  },

  argon: {
    dark: {
      '--t-bg0': '#111c44',
      '--t-bg0g': '#051139',
      '--t-bg1': '#051139',
      '--t-bg2': 'rgba(30, 41, 71, 0.8)',
      '--t-bgGlass': 'rgba(30, 41, 71, 0.8)',
      '--t-bgGlassLt': 'rgba(30, 41, 71, 0.6)',
      '--t-surfaceSolid': '#1a2035',
      '--t-border': 'rgba(98, 117, 148, 0.4)',
      '--t-borderHi': 'rgba(94, 114, 228, 0.6)',
      '--t-white': '#ffffff',
      '--t-textLt': '#ced4da',
      '--t-muted': '#ced4da',
      '--t-accentSoft': 'rgba(94, 114, 228, 0.15)',
      '--t-accent': '#5e72e4',
      '--t-badgeBg': 'rgba(255, 255, 255, 0.05)',
      '--t-sliderBg': '#3a416f',
      '--t-sliderActive': '#5e72e4',
      '--t-bodyBg': 'linear-gradient(145deg, #111c44 0%, #051139 100%)',
    },
    light: {
      '--t-bg0': '#f8fafc',
      '--t-bg0g': '#f1f5f9',
      '--t-bg1': '#ffffff',
      '--t-bg2': 'rgba(255, 255, 255, 0.9)',
      '--t-bgGlass': 'rgba(255, 255, 255, 0.9)',
      '--t-bgGlassLt': 'rgba(255, 255, 255, 0.7)',
      '--t-surfaceSolid': '#ffffff',
      '--t-border': '#e9ecef',
      '--t-borderHi': '#5e72e4',
      '--t-white': '#344767',
      '--t-textLt': '#67748e',
      '--t-muted': '#adb5bd',
      '--t-accentSoft': 'rgba(94, 114, 228, 0.1)',
      '--t-accent': '#5e72e4',
      '--t-badgeBg': 'rgba(0, 0, 0, 0.04)',
      '--t-sliderBg': '#e9ecef',
      '--t-sliderActive': '#5e72e4',
      '--t-bodyBg': 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)',
    },
  },

  datta: {
    dark: {
      '--t-bg0': '#212224',
      '--t-bg0g': '#1a1b1d',
      '--t-bg1': '#2b2c2f',
      '--t-bg2': 'rgba(43, 44, 47, 0.85)',
      '--t-bgGlass': 'rgba(43, 44, 47, 0.85)',
      '--t-bgGlassLt': 'rgba(43, 44, 47, 0.65)',
      '--t-surfaceSolid': '#2b2c2f',
      '--t-border': '#393b3f',
      '--t-borderHi': '#04A9F5',
      '--t-white': 'rgba(255, 255, 255, 0.8)',
      '--t-textLt': '#bfbfbf',
      '--t-muted': '#bfbfbf',
      '--t-accentSoft': 'rgba(4, 169, 245, 0.15)',
      '--t-accent': '#04A9F5',
      '--t-badgeBg': 'rgba(255, 255, 255, 0.05)',
      '--t-sliderBg': '#393b3f',
      '--t-sliderActive': '#04A9F5',
      '--t-bodyBg': 'linear-gradient(145deg, #212224 0%, #1a1b1d 100%)',
    },
    light: {
      '--t-bg0': '#f4f7fa',
      '--t-bg0g': '#ebf0f5',
      '--t-bg1': '#3f4d67',
      '--t-bg2': 'rgba(255, 255, 255, 0.95)',
      '--t-bgGlass': 'rgba(255, 255, 255, 0.95)',
      '--t-bgGlassLt': 'rgba(255, 255, 255, 0.75)',
      '--t-surfaceSolid': '#ffffff',
      '--t-border': '#f1f1f1',
      '--t-borderHi': '#04A9F5',
      '--t-white': '#1d2630',
      '--t-textLt': '#888888',
      '--t-muted': '#5b6b79',
      '--t-accentSoft': 'rgba(4, 169, 245, 0.1)',
      '--t-accent': '#04A9F5',
      '--t-badgeBg': 'rgba(0, 0, 0, 0.05)',
      '--t-sliderBg': '#bec8d0',
      '--t-sliderActive': '#04A9F5',
      '--t-bodyBg': 'linear-gradient(145deg, #f4f7fa 0%, #ebf0f5 100%)',
    },
  },

  cosmic: {
    dark: {
      '--t-bg0': '#05070c',
      '--t-bg0g': '#03060c',
      '--t-bg1': 'rgba(8, 14, 23, 0.65)',
      '--t-bg2': 'rgba(12, 18, 28, 0.68)',
      '--t-bgGlass': 'rgba(12, 18, 28, 0.68)',
      '--t-bgGlassLt': 'rgba(12, 18, 28, 0.45)',
      '--t-surfaceSolid': '#0c121c',
      '--t-border': 'rgba(72, 187, 255, 0.2)',
      '--t-borderHi': 'rgba(0, 230, 255, 0.35)',
      '--t-white': '#f0f3f8',
      '--t-textLt': '#f0f3f8',
      '--t-muted': '#a3b3cc',
      '--t-accentSoft': 'rgba(43, 154, 255, 0.12)',
      '--t-accent': '#2b9aff',
      '--t-badgeBg': 'rgba(255, 255, 255, 0.03)',
      '--t-sliderBg': 'rgba(43, 154, 255, 0.25)',
      '--t-sliderActive': '#00c8ff',
      '--t-bodyBg': 'linear-gradient(145deg, #05070c 0%, #03060c 100%)',
    },
    light: {
      '--t-bg0': '#f8fafc',
      '--t-bg0g': '#f0f5fa',
      '--t-bg1': '#f0f5fa',
      '--t-bg2': 'rgba(255, 255, 255, 0.75)',
      '--t-bgGlass': 'rgba(255, 255, 255, 0.75)',
      '--t-bgGlassLt': 'rgba(255, 255, 255, 0.55)',
      '--t-surfaceSolid': '#ffffff',
      '--t-border': 'rgba(203, 213, 225, 0.6)',
      '--t-borderHi': 'rgba(59, 130, 246, 0.45)',
      '--t-white': '#0b1e33',
      '--t-textLt': '#1e293b',
      '--t-muted': '#475569',
      '--t-accentSoft': '#e6edf6',
      '--t-accent': '#3b82f6',
      '--t-badgeBg': 'rgba(0, 0, 0, 0.06)',
      '--t-sliderBg': '#b9c9e0',
      '--t-sliderActive': '#3b82f6',
      '--t-bodyBg': 'linear-gradient(145deg, #f8fafc 0%, #f0f5fa 100%)',
    },
  },
};

function applyThemeVars(themeId, isDark) {
  const vars = THEMES[themeId]?.[isDark ? 'dark' : 'light'] || THEMES['tsela'][isDark ? 'dark' : 'light'];
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute('data-theme', `${themeId}-${isDark ? 'dark' : 'light'}`);
}

// ── Inject global CSS once ────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');

  /* ── Tsela Deep Glass Brand Tokens ───────────────────────────────────── */
  :root {
    --deep-sky-blue:        #2C72AE;
    --light-aviation-blue:  #5F98C4;
    --pale-steel-blue:      #ACBECA;
    --golden-yellow:        #C6A744;

    /* Deep Glass dark defaults */
    --glass-fill:         rgba(255,255,255,0.04);
    --glass-fill-hover:   rgba(255,255,255,0.065);
    --glass-border:       rgba(255,255,255,0.08);
    --glass-border-hover: rgba(255,255,255,0.14);
    --glass-sheen:        linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent);

    --accent-glass-fill:   rgba(198,167,68,0.07);
    --accent-glass-border: rgba(198,167,68,0.22);
    --accent-glass-sheen:  linear-gradient(90deg, transparent, rgba(198,167,68,0.25), transparent);

    --blue-glass-fill:   rgba(44,114,174,0.18);
    --blue-glass-border: rgba(95,152,196,0.25);

    --blur-sm: 16px;
    --blur-md: 24px;
    --blur-lg: 32px;

    --text-primary:   rgba(255,255,255,0.88);
    --text-secondary: rgba(255,255,255,0.45);
    --text-tertiary:  rgba(255,255,255,0.25);
    --text-blue:      #5F98C4;
    --text-gold:      #C6A744;

    --status-up:   rgba(62,169,109,0.8);
    --status-warn: rgba(198,167,68,0.9);
  }

  *, *::before, *::after { box-sizing: border-box; }

  body {
    font-family: 'DM Sans', 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 14px;
    line-height: 1.5;
  }

  div, header, nav, main, footer, button, a, span, p, label, input, select, textarea {
    transition: background-color 0.3s ease, border-color 0.3s ease,
                box-shadow 0.3s ease, color 0.3s ease;
  }

  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: var(--t-bg0); }
  ::-webkit-scrollbar-thumb { background: var(--t-accent); border-radius: 3px; }

  /* ══════════════════════════════════════════════════════════════════════
     DEEP GLASS — DARK MODE  (default, no selector needed)
     ══════════════════════════════════════════════════════════════════════ */

  /* Glass panel */
  .dg-panel {
    background: var(--glass-fill);
    backdrop-filter: blur(var(--blur-md));
    -webkit-backdrop-filter: blur(var(--blur-md));
    border: 1px solid var(--glass-border);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
  }
  .dg-panel::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: var(--glass-sheen);
    pointer-events: none;
    z-index: 1;
  }

  /* KPI Card */
  .dg-kpi {
    background: var(--glass-fill);
    backdrop-filter: blur(var(--blur-md));
    -webkit-backdrop-filter: blur(var(--blur-md));
    border: 1px solid var(--glass-border);
    border-radius: 20px;
    padding: 24px;
    position: relative;
    overflow: hidden;
    transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
    cursor: default;
  }
  .dg-kpi::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: var(--glass-sheen);
    pointer-events: none;
  }
  .dg-kpi:hover { border-color: var(--glass-border-hover); transform: translateY(-2px); }
  .dg-kpi.accent { background: var(--accent-glass-fill); border-color: var(--accent-glass-border); }
  .dg-kpi.accent::before { background: var(--accent-glass-sheen); }
  .dg-kpi.accent:hover { border-color: rgba(198,167,68,0.35); }

  /* Sidebar */
  .dg-sidebar {
    background: rgba(6,16,26,0.88) !important;
    backdrop-filter: blur(var(--blur-md)) !important;
    -webkit-backdrop-filter: blur(var(--blur-md)) !important;
    border-right: 1px solid var(--glass-border) !important;
    position: relative;
  }

  /* Topbar */
  .dg-topbar {
    background: rgba(8,20,32,0.6) !important;
    backdrop-filter: blur(var(--blur-md)) !important;
    -webkit-backdrop-filter: blur(var(--blur-md)) !important;
    border-bottom: 1px solid var(--glass-border) !important;
    position: relative;
  }
  .dg-topbar::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: var(--glass-sheen);
    pointer-events: none;
  }

  /* Nav link */
  .dg-nav-link { border-radius: 10px; transition: background 0.2s, color 0.2s; }
  .dg-nav-link:hover { background: rgba(255,255,255,0.05) !important; }
  .dg-nav-link.active {
    background: var(--blue-glass-fill) !important;
    border: 1px solid var(--blue-glass-border) !important;
  }

  /* Status chips */
  .dg-chip {
    display: inline-flex; align-items: center;
    padding: 4px 10px; border-radius: 99px;
    font-size: 11px; font-weight: 600;
    border: 1px solid; white-space: nowrap;
  }
  .dg-chip.pending   { color: var(--golden-yellow);        border-color: rgba(198,167,68,0.3);   background: rgba(198,167,68,0.08); }
  .dg-chip.active    { color: var(--golden-yellow);        border-color: rgba(198,167,68,0.3);   background: rgba(198,167,68,0.08); }
  .dg-chip.assessing { color: var(--light-aviation-blue);  border-color: rgba(95,152,196,0.3);   background: rgba(95,152,196,0.08); }
  .dg-chip.qa        { color: rgba(155,89,182,0.9);        border-color: rgba(155,89,182,0.3);   background: rgba(155,89,182,0.08); }
  .dg-chip.complete  { color: rgba(62,169,109,0.9);        border-color: rgba(62,169,109,0.3);   background: rgba(62,169,109,0.07); }
  .dg-chip.on_hold   { color: var(--pale-steel-blue);      border-color: rgba(172,190,202,0.2);  background: rgba(172,190,202,0.05); }

  /* Progress bar */
  .dg-bar-track { height: 4px; border-radius: 99px; background: rgba(255,255,255,0.07); overflow: hidden; }
  .dg-bar-fill  { height: 100%; border-radius: 99px; background: linear-gradient(90deg, var(--deep-sky-blue), var(--light-aviation-blue)); }
  .dg-bar-fill.gold { background: linear-gradient(90deg, var(--golden-yellow), #d4b55a); }

  /* Buttons */
  .dg-btn {
    border-radius: 12px; padding: 9px 20px;
    font-size: 14px; font-weight: 500;
    font-family: inherit;
    cursor: pointer; transition: all 0.2s;
    line-height: 1.4;
  }
  .dg-btn-primary {
    background: var(--deep-sky-blue); color: white; border: none;
    box-shadow: 0 4px 14px rgba(44,114,174,0.35);
  }
  .dg-btn-primary:hover { background: #235d8f; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(44,114,174,0.45); }
  .dg-btn-ghost {
    background: var(--glass-fill); border: 1px solid var(--glass-border);
    color: var(--text-secondary); backdrop-filter: blur(var(--blur-sm));
  }
  .dg-btn-ghost:hover { background: var(--glass-fill-hover); color: var(--text-primary); }
  .dg-btn-danger {
    background: rgba(232,64,64,0.12); border: 1px solid rgba(232,64,64,0.3); color: #e84040;
  }
  .dg-btn-danger:hover { background: rgba(232,64,64,0.2); }

  /* Row hovers */
  .dg-row-hover { transition: background 0.15s; cursor: default; }
  .dg-row-hover:hover { background: rgba(255,255,255,0.03); }
  .dg-act-item { transition: background 0.15s; }
  .dg-act-item:hover { background: rgba(255,255,255,0.025); }

  /* Calendar day */
  .dg-cal-day { transition: background 0.15s, border-color 0.15s; }
  .dg-cal-day:hover { border-color: rgba(95,152,196,0.3) !important; }

  /* Inputs */
  .dg-input {
    background: rgba(6,16,26,0.6);
    border: 1px solid var(--glass-border);
    border-radius: 8px;
    color: var(--text-primary);
    font-size: 13px;
    padding: 8px 12px;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
    transition: border-color 0.2s;
  }
  .dg-input:focus { border-color: var(--blue-glass-border); }
  .dg-input::placeholder { color: var(--text-tertiary); }
  .dg-input option { background: #0d1e30; }

  /* Modal */
  .dg-modal-overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(4,9,9,0.75);
    backdrop-filter: blur(var(--blur-lg));
    -webkit-backdrop-filter: blur(var(--blur-lg));
    display: flex; align-items: center; justify-content: center;
    animation: tselaFadeIn 0.2s ease-out;
  }
  .dg-modal {
    background: rgba(13,30,48,0.97);
    border: 1px solid var(--glass-border);
    border-radius: 20px;
    position: relative;
    overflow: hidden;
    animation: tselaModalIn 0.3s ease-out;
  }
  .dg-modal::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; height: 1px;
    background: var(--glass-sheen);
    pointer-events: none;
  }

  /* ══════════════════════════════════════════════════════════════════════
     HORIZON — LIGHT MODE  [data-theme="deep_glass-light"]
     Applies as the deep_glass light variant.
     Font: Plus Jakarta Sans. Text: black-family only (#040909).
     Glass: white-fill + white-border + box-shadow. No sheen lines.
     ══════════════════════════════════════════════════════════════════════ */

  [data-theme="deep_glass-light"] {
    /* Override glass tokens */
    --glass-fill:         rgba(255,255,255,0.55);
    --glass-fill-hover:   rgba(255,255,255,0.72);
    --glass-border:       rgba(255,255,255,0.75);
    --glass-border-hover: rgba(255,255,255,0.90);

    --blue-glass-fill:   rgba(44,114,174,0.10);
    --blue-glass-border: rgba(44,114,174,0.22);

    --accent-glass-fill:   rgba(198,167,68,0.10);
    --accent-glass-border: rgba(198,167,68,0.28);

    /* Override blur amounts — Horizon uses slightly tighter blur */
    --blur-sm: 14px;
    --blur-md: 20px;
    --blur-lg: 28px;

    /* Override text tokens — black-family only */
    --text-primary:   #040909;
    --text-secondary: rgba(4,9,9,0.65);
    --text-tertiary:  rgba(4,9,9,0.40);
    --text-blue:      #2C72AE;
    --text-gold:      #614A07;

    --status-up:   #2a7a4f;
    --status-warn: #614A07;
  }

  /* Horizon font override */
  [data-theme="deep_glass-light"],
  [data-theme="deep_glass-light"] body,
  [data-theme="deep_glass-light"] button,
  [data-theme="deep_glass-light"] input,
  [data-theme="deep_glass-light"] select,
  [data-theme="deep_glass-light"] textarea {
    font-family: 'Plus Jakarta Sans', sans-serif !important;
  }

  /* Horizon panel — white glass + shadow, no sheen */
  [data-theme="deep_glass-light"] .dg-panel {
    background: rgba(255,255,255,0.55) !important;
    border-color: rgba(255,255,255,0.75) !important;
    box-shadow: 0 4px 20px rgba(44,114,174,0.07);
  }
  [data-theme="deep_glass-light"] .dg-panel::before { display: none; }

  /* Horizon KPI card */
  [data-theme="deep_glass-light"] .dg-kpi {
    background: rgba(255,255,255,0.55) !important;
    border-color: rgba(255,255,255,0.75) !important;
    box-shadow: 0 4px 20px rgba(44,114,174,0.08);
  }
  [data-theme="deep_glass-light"] .dg-kpi::before { display: none; }
  [data-theme="deep_glass-light"] .dg-kpi:hover {
    border-color: rgba(255,255,255,0.90) !important;
    box-shadow: 0 8px 28px rgba(44,114,174,0.14);
    transform: translateY(-2px);
  }
  [data-theme="deep_glass-light"] .dg-kpi.accent {
    background: linear-gradient(135deg, rgba(198,167,68,0.08), rgba(255,255,255,0.55)) !important;
    border-color: rgba(198,167,68,0.28) !important;
  }

  /* Horizon sidebar */
  [data-theme="deep_glass-light"] .dg-sidebar {
    background: rgba(255,255,255,0.30) !important;
    border-right-color: rgba(255,255,255,0.45) !important;
  }

  /* Horizon topbar — white glass, no sheen */
  [data-theme="deep_glass-light"] .dg-topbar {
    background: rgba(255,255,255,0.40) !important;
    border-bottom-color: rgba(255,255,255,0.60) !important;
  }
  [data-theme="deep_glass-light"] .dg-topbar::after { display: none; }

  /* Horizon nav links */
  [data-theme="deep_glass-light"] .dg-nav-link:hover {
    background: rgba(255,255,255,0.40) !important;
    color: #040909 !important;
  }
  [data-theme="deep_glass-light"] .dg-nav-link.active {
    background: rgba(255,255,255,0.70) !important;
    border-color: rgba(255,255,255,0.80) !important;
    color: #2C72AE !important;
    box-shadow: 0 2px 12px rgba(44,114,174,0.12);
  }

  /* Horizon chips — black-family text */
  [data-theme="deep_glass-light"] .dg-chip.pending   { color: #614A07;          border-color: rgba(198,167,68,0.30); background: rgba(198,167,68,0.10); }
  [data-theme="deep_glass-light"] .dg-chip.active    { color: #614A07;          border-color: rgba(198,167,68,0.30); background: rgba(198,167,68,0.10); }
  [data-theme="deep_glass-light"] .dg-chip.assessing { color: #2C72AE;          border-color: rgba(44,114,174,0.28); background: rgba(44,114,174,0.08); }
  [data-theme="deep_glass-light"] .dg-chip.qa        { color: rgba(4,9,9,0.55); border-color: rgba(4,9,9,0.15);     background: rgba(4,9,9,0.05);      }
  [data-theme="deep_glass-light"] .dg-chip.complete  { color: #2a5e40;          border-color: rgba(42,94,64,0.28);  background: rgba(42,94,64,0.07);   }
  [data-theme="deep_glass-light"] .dg-chip.on_hold   { color: rgba(4,9,9,0.55); border-color: rgba(4,9,9,0.15);     background: rgba(4,9,9,0.05);      }

  /* Horizon progress bar — near-black track */
  [data-theme="deep_glass-light"] .dg-bar-track { background: rgba(4,9,9,0.07); }
  [data-theme="deep_glass-light"] .dg-bar-track { height: 5px; }

  /* Horizon ghost button — visible outlined style on light background */
  [data-theme="deep_glass-light"] .dg-btn-ghost {
    background: rgba(255,255,255,0.65) !important;
    border-color: rgba(4,9,9,0.15) !important;
    color: #040909 !important;
    box-shadow: 0 1px 6px rgba(44,114,174,0.07) !important;
  }
  [data-theme="deep_glass-light"] .dg-btn-ghost:hover {
    background: rgba(255,255,255,0.88) !important;
    border-color: rgba(44,114,174,0.30) !important;
    box-shadow: 0 2px 10px rgba(44,114,174,0.12) !important;
  }

  /* Horizon row hovers */
  [data-theme="deep_glass-light"] .dg-row-hover:hover { background: rgba(255,255,255,0.35); }
  [data-theme="deep_glass-light"] .dg-act-item:hover  { background: rgba(255,255,255,0.35); }

  /* Horizon calendar day */
  [data-theme="deep_glass-light"] .dg-cal-day {
    background: rgba(255,255,255,0.35) !important;
    border-color: rgba(255,255,255,0.55) !important;
  }
  [data-theme="deep_glass-light"] .dg-cal-day:hover { border-color: rgba(44,114,174,0.30) !important; }

  /* Horizon input */
  [data-theme="deep_glass-light"] .dg-input {
    background: rgba(255,255,255,0.55) !important;
    border-color: rgba(255,255,255,0.75) !important;
    color: #040909 !important;
  }
  [data-theme="deep_glass-light"] .dg-input::placeholder { color: rgba(4,9,9,0.35) !important; }
  [data-theme="deep_glass-light"] .dg-input option { background: #dce8f0 !important; }
  [data-theme="deep_glass-light"] .dg-input:focus { border-color: rgba(44,114,174,0.40) !important; }

  /* Horizon modal */
  [data-theme="deep_glass-light"] .dg-modal-overlay {
    background: rgba(44,114,174,0.15);
  }
  [data-theme="deep_glass-light"] .dg-modal {
    background: rgba(255,255,255,0.88) !important;
    border-color: rgba(255,255,255,0.85) !important;
    box-shadow: 0 20px 60px rgba(44,114,174,0.15);
  }
  [data-theme="deep_glass-light"] .dg-modal::before { display: none; }

  /* Horizon scrollbar */
  [data-theme="deep_glass-light"] ::-webkit-scrollbar-track { background: rgba(44,114,174,0.05); }
  [data-theme="deep_glass-light"] ::-webkit-scrollbar-thumb { background: rgba(44,114,174,0.25); }

  /* Safety net: force black-family text everywhere in Horizon light — catches any remaining
     hardcoded rgba(255,255,255,...) inline styles in assessment pages, reports, etc. */
  [data-theme="deep_glass-light"] main *,
  [data-theme="deep_glass-light"] .dg-sidebar *,
  [data-theme="deep_glass-light"] .dg-topbar * { color: #040909 !important; }

  /* Restore intentional colored elements (must also use !important to override the rule above) */
  [data-theme="deep_glass-light"] .dg-nav-link.active,
  [data-theme="deep_glass-light"] .dg-nav-link.active * { color: #2C72AE !important; }
  [data-theme="deep_glass-light"] .dg-chip.pending,
  [data-theme="deep_glass-light"] .dg-chip.active   { color: #614A07 !important; }
  [data-theme="deep_glass-light"] .dg-chip.assessing { color: #2C72AE !important; }
  [data-theme="deep_glass-light"] .dg-chip.complete  { color: #2a5e40 !important; }
  [data-theme="deep_glass-light"] .dg-chip.qa,
  [data-theme="deep_glass-light"] .dg-chip.on_hold   { color: rgba(4,9,9,0.55) !important; }
  [data-theme="deep_glass-light"] .dg-btn-primary    { color: #ffffff !important; }
  [data-theme="deep_glass-light"] .dg-btn-danger     { color: #e84040 !important; }

  /* ── Animations ──────────────────────────────────────────────────────── */
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes tselaFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes tselaModalIn {
    from { opacity: 0; transform: scale(0.95) translateY(10px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
`;

function injectGlobalStyles() {
  if (document.getElementById('tsela-theme-globals')) return;
  const style = document.createElement('style');
  style.id = 'tsela-theme-globals';
  style.textContent = GLOBAL_CSS;
  document.head.appendChild(style);
}

// ── Context ───────────────────────────────────────────────────────────────────
const ThemeCtx = createContext({
  themeId: 'deep_glass',
  isDark: true,
  setThemeId: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => localStorage.getItem('tsela-theme-id') || 'deep_glass');
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('tsela-theme-dark');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    injectGlobalStyles();
    applyThemeVars(themeId, isDark);
  }, []); // eslint-disable-line

  useEffect(() => {
    applyThemeVars(themeId, isDark);
    localStorage.setItem('tsela-theme-id', themeId);
    localStorage.setItem('tsela-theme-dark', isDark ? 'dark' : 'light');
  }, [themeId, isDark]);

  return (
    <ThemeCtx.Provider value={{ themeId, isDark, setThemeId, toggleTheme: () => setIsDark(d => !d) }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
